import {
  useState,
} from "react";
import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

interface PhoneInputProps {
  /** Id of the number input; the country picker gets `${id}-country`. */
  id: string;
  /** ISO region the picker starts on (the owner's, when known). */
  defaultRegion?: string | null | undefined;
  /**
   * Seeds the number on mount. Uncontrolled afterwards, like
   * SegmentedDateInput — a parent applying a new value remounts it via `key`.
   */
  initialValue?: string | undefined;
  /** Called with the number to submit (see `composePhone`). */
  onChange: (value: string) => void;
  /**
   * Id of the message saying why the server refused this number. The number
   * box is marked invalid and described by it.
   */
  errorId?: string | undefined;
}

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

const COUNTRIES = getCountries()
  .map((code) => ({
    code,
    label: `${regionNames.of(code) ?? code} (+${getCountryCallingCode(code)})`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const KNOWN = new Set<string>(getCountries());

function asCountry(region: string | null | undefined): CountryCode | "" {
  return region && KNOWN.has(region) ? (region as CountryCode) : "";
}

/**
 * The number to submit: read in the picked country unless it states its own
 * code, so `(469) 555-5668` with United States becomes `+14695555668` and a
 * typed `+44 20 8016 0508` keeps its `+44`. Anything that isn't a valid number
 * there goes as typed, for the server to refuse with its reason: a length
 * check alone would send an Israeli `054-260-0200` with United States picked
 * as `+10542600200`, which the server only shape-checks and would save.
 */
export function composePhone(region: string, text: string): string {
  const parsed = parsePhoneNumberFromString(text, asCountry(region) || undefined);
  return parsed?.isValid() ? parsed.number : text;
}

export function PhoneInput({
  id,
  defaultRegion,
  initialValue,
  onChange,
  errorId,
}: PhoneInputProps) {
  // A value that already carries its country opens beside that country.
  const [region, setRegion] = useState<string>(
    () =>
      parsePhoneNumberFromString(initialValue ?? "")?.country ??
      asCountry(defaultRegion),
  );
  const [text, setText] = useState(initialValue ?? "");

  const update = (nextRegion: string, nextText: string) => {
    setRegion(nextRegion);
    setText(nextText);
    onChange(nextText ? composePhone(nextRegion, nextText) : "");
  };

  return (
    <div className="flex gap-2">
      <select
        id={`${id}-country`}
        aria-label="Country code"
        value={region}
        onChange={(e) => update(e.target.value, text)}
        className="block w-40 shrink-0 rounded-md border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:border-catch-red focus:ring-catch-red px-2 py-2 border"
      >
        <option value="">Country</option>
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        ))}
      </select>
      <input
        id={id}
        type="tel"
        autoComplete="off"
        required
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        value={text}
        onChange={(e) => update(region, e.target.value)}
        className={`block w-full rounded-md ${errorId ? "border-red-400" : "border-gray-300"} shadow-sm focus:outline-none focus:ring-2 focus:border-catch-red focus:ring-catch-red px-3 py-2 border`}
      />
    </div>
  );
}
